import { useProductGridController, useProductGridFilters, useProductGridSortBy } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.controller';
import { useProductGridHealth, wrapWithProductGridHealthProvider } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.health';
import { ProductTileSkeleton } from '@apps/registry/common/components/Catalog/components/ProductTile/ProductTile';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useWindowResizeObserver } from '@shared/utils/hooks/useWindowResize';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { BoxProps, ButtonV2, Flex, Box, TextV2 } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { Breadcrumbs } from '..';
import { ExtractCatalogProductComponentSource, PagesForTelemetry } from '../..';
import { UseSharedCatalogMutationReturn } from '../../Catalog.controller';
import { CatalogProductListFilters, UniversalItem, isCatalogItem, isExternalProductOffer } from '../../Catalog.types';
import { useLineageTitles } from '../../hooks/useLineageTitles';
import { useCatalogContext } from '../../state/Catalog';
import { ActiveRefinementsBar } from '../ActiveRefinementsBar/ActiveRefinementsBar';
import { CustomCashItemTile } from '../PreviewAndEditCatalogCashItemDialog';
import { ProductTile } from '../ProductTile';
import { MobileProductQuickAddDrawer, useMobileProductQuickAddDrawerProvider } from '../ProductTile/components/MobileProductQuickAddDrawer';
import { FloatingRefinementPanelToggle, RefinementPanelHeaderToggle, RefinementPanelTopBarToggle } from '../RefinementPanelToggle/RefinementPanelToggle';
import { RefinementsPanel } from '../RefinementsPanel';
import { RefinementsPanelMobileDrawer } from '../RefinementsPanelMobileDrawer';
import { SelectedCategoryText } from '../SelectedCategoryText';
import { MaxColumnType, StyledGridContainer, StyledMain, StyledRoot, StyledWrapper, styles } from './ProductGrid.styles';
import { CatalogStorefront, ProductCatalogSearchQueryOrderBy } from '@graphql/generated';
import { useShopStorefrontContext } from '../ShopStorefrontProvider';
import { ProductGridHighlights, useProductGridHighlights } from './components/Highlights';
import { selectedProductToRegistryPayload, useExternalProductsWithModal } from '../UniversalProductSearch';
import { useCatalogSearchBarContext } from '../CatalogSearchBar';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useTranslation } from '@shared/core';
import { ProgressBar } from '@shared/components/ProgressBar';
import { pxToRem } from '@withjoy/joykit/theme';

interface ProductGridProps {
  products: ReadonlyArray<UniversalItem>;
  addCatalogItem: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  addItemLoading: boolean;
  isMobile?: boolean;
  showSkeleton?: boolean;
  searchLoading?: boolean;
  disableFilters?: boolean;
  shouldShowValuePropCards?: boolean;
  shouldShowValuePropsSection?: boolean;
  hideHeader?: boolean;
  totalProductCount?: number;

  /**
   * Should be the public url path
   */
  categoryLocator: Maybe<string>;
  categoryType: 'curation' | 'category';
  componentSource: ExtractCatalogProductComponentSource<'productGrid' | 'productRec'>;
  telemetryPageValue: PagesForTelemetry;
  hasNextPage: boolean;
  isFetchingMore: boolean;
  onLoadMoreClick: Maybe<() => void>;
  filters: CatalogProductListFilters;
  hideFilterToggle?: boolean;

  shouldOpenPdp?: boolean;
  maxColumns?: MaxColumnType;
  maxWidth?: BoxProps['maxWidth'];
}

interface ProductGridExtendedProps extends ProductGridProps {
  disableGridMarginBottom: boolean;
  sortBy?: ProductCatalogSearchQueryOrderBy;
}

const PlaceholderTiles = ({ count, isMobile }: { count: number; isMobile?: boolean }) => {
  return (
    <>
      {Array.from(Array(count)).map((_, idx) => {
        return (
          <ProductTileSkeleton
            variant="gridItem"
            key={idx}
            isMobile={isMobile}
          />
        );
      })}
    </>
  );
};

const Wrapper: React.FC<BoxProps & { isEmptyState: boolean }> = ({ isEmptyState, children }) => {
  return <StyledWrapper __css={styles.wrapper({ isEmptyState })}>{children}</StyledWrapper>;
};

const Main: React.FC<BoxProps> = props => {
  return (
    <StyledMain
      __css={styles.main}
      {...props}
    />
  );
};

const ProductGridContainer = React.forwardRef<
  HTMLDivElement,
  { isMobile: boolean | undefined; maxColumns?: MaxColumnType; applyEmptyStateStyles: boolean; children: React.ReactNode }
>(({ children, isMobile, applyEmptyStateStyles, maxColumns }, ref) => {
  return (
    <StyledGridContainer
      isMobile={isMobile}
      __css={applyEmptyStateStyles ? styles.emptyProductList : styles.nonZeroProductList(maxColumns)}
      width="100%"
      ref={ref}
    >
      {children}
    </StyledGridContainer>
  );
});

const Root: React.FC<ProductGridProps> = props => {
  const {
    componentSource,
    products,
    filters,
    showSkeleton,
    addCatalogItem,
    addItemLoading,
    categoryLocator,
    categoryType,
    isMobile,
    hasNextPage,
    isFetchingMore,
    maxColumns,
    onLoadMoreClick,
    shouldShowValuePropCards,
    shouldOpenPdp = true,
    telemetryPageValue,
    totalProductCount,
  } = props;
  const {
    deriveTelemetryPageForProduct,
    showCustomCashTile,
    handleClickForCashDialog,
    fundType,
    handleOnCustomCashTileClick,
    hasAnyStorefrontWarning,
    productGridItems,
    isProductInRegistry,
  } = useProductGridController({
    categoryLocator: categoryLocator || null,
    categoryType,
    products,
    filters,
    showSkeleton,
    shouldShowValuePropCards,
    isMobile,
  });
  const { trackUniversalSearchResultClickAction } = useCatalogLoggingContext();
  const { subCatHighlightsVisible, subCatHighlightsIndex } = useProductGridHighlights();
  const { searchQuery } = useCatalogSearchBarContext();
  const { t } = useTranslation('sharedComponents');
  const { viewMoreTitle, productCountSummary } = t('viewMore');
  const progressPercentage = totalProductCount ? (products.length / totalProductCount) * 100 : 0;

  const { renderExternalProduct, renderExternalProductModal } = useExternalProductsWithModal({
    handleProductAdd: args => {
      const payload = selectedProductToRegistryPayload({ ...args, page: telemetryPageValue, searchQuery, component: 'productGrid' });
      addCatalogItem(payload);
    },
    handleProductClick: ({ itemPosition, offer }) => {
      trackUniversalSearchResultClickAction({
        label: 'grid',
        component: 'productGrid',
        itemPosition,
        searchResult: offer,
        gridPlacement: 'primary',
        joyCatalogSearchResultCount: totalProductCount,
        visibleJoyCatalogSearchResultsCount: products.length,
        searchTermOverride: searchQuery,
        page: telemetryPageValue,
      });
    },
  });
  const { currentProductId: currentQuickAddProductId, currentProductIndex: currentQuickAddProductIndex } = useMobileProductQuickAddDrawerProvider();

  useWindowResizeObserver();

  // the Group Catalog Item should be enabled only in the ProductGrid
  return (
    <>
      <ProductGridContainer
        maxColumns={maxColumns}
        isMobile={isMobile}
        applyEmptyStateStyles={products.length === 0}
      >
        {showCustomCashTile && (
          <CustomCashItemTile
            fundType={fundType}
            onClick={handleOnCustomCashTileClick}
          />
        )}
        {productGridItems.map((productGridItem, index) => (
          <React.Fragment key={'fragment-' + index}>
            {subCatHighlightsVisible && index === subCatHighlightsIndex && <ProductGridHighlights positionIndex={index} />}
            {productGridItem.type === 'valueProp' && <React.Fragment key={productGridItem.type + index}>{productGridItem.valueProp}</React.Fragment>}
            {productGridItem.type === 'product' && isCatalogItem(productGridItem.product) && (
              <ProductTile
                index={index}
                key={productGridItem.product.id}
                addItemLoading={addItemLoading}
                product={productGridItem.product}
                deriveTelemetryPageForProduct={deriveTelemetryPageForProduct}
                addCatalogItem={addCatalogItem}
                categoryType={categoryType}
                handleClickForCashDialog={handleClickForCashDialog}
                isMobile={isMobile}
                hasAnyStorefrontWarning={hasAnyStorefrontWarning}
                componentSource={componentSource}
                variant={'gridItem'}
                shouldOpenPdp={shouldOpenPdp}
                isItemAddedToRegistry={isProductInRegistry(productGridItem?.product?.id)}
              />
            )}
            {productGridItem.type === 'product' &&
              isExternalProductOffer(productGridItem.product) &&
              renderExternalProduct({
                index,
                isMobile,
                keyPrefix: 'productGrid-',
                offer: productGridItem.product,
              })}
          </React.Fragment>
        ))}
        {isFetchingMore && (
          <PlaceholderTiles
            count={6}
            isMobile={props.isMobile}
          />
        )}
      </ProductGridContainer>
      {renderExternalProductModal()}
      <MobileProductQuickAddDrawer
        addCatalogItem={addCatalogItem}
        addItemLoading={addItemLoading}
        deriveTelemetryPageForProduct={deriveTelemetryPageForProduct}
        categoryType={categoryType}
        handleClickForCashDialog={handleClickForCashDialog}
        catalogItemId={currentQuickAddProductId}
        index={currentQuickAddProductIndex || 0}
        hasAnyStorefrontWarning={hasAnyStorefrontWarning}
        componentSource={componentSource}
        variant={'gridItem'}
        isItemAddedToRegistry={isProductInRegistry(currentQuickAddProductId || '')}
      />
      {hasNextPage && onLoadMoreClick && (
        <Flex
          justifyContent="center"
          marginTop={[9, '80px']}
          flexDirection="column"
          alignItems="center"
          width={isMobile ? '100%' : 300}
          paddingX={24}
        >
          <TextV2
            typographyVariant="label1"
            marginBottom={3}
          >
            {productCountSummary({ count: products.length, total: totalProductCount })}
          </TextV2>
          <ProgressBar
            progressPercentage={progressPercentage}
            height={12}
            gradientFill="linear-gradient(90deg, color(display-p3 0.337255 0 .95), color(display-p3 0.286275 0.317647 0.937255))"
          />
          <ButtonV2
            intent="neutral"
            width="100%"
            borderRadius={pxToRem(24)}
            padding={6}
            _active={{ color: 'mono14' }}
            _hover={{ color: 'white', backgroundColor: 'mono11' }}
            loading={isFetchingMore}
            onClick={onLoadMoreClick}
            marginTop={6}
          >
            {viewMoreTitle()}
          </ButtonV2>
        </Flex>
      )}
    </>
  );
};

export const ProductGrid: React.FC<ProductGridExtendedProps> = wrapWithProductGridHealthProvider(props => {
  const {
    filters,
    sortBy,
    showSkeleton,
    disableGridMarginBottom,
    hideFilterToggle,
    disableFilters,
    shouldShowValuePropsSection: showValuePropsSection,
    hideHeader = false,
    products,
    maxColumns,
    maxWidth,
    ...restProps
  } = props;
  const lineageTitles = useLineageTitles();
  const { selectedCategory, isAdminView } = useCatalogContext();
  const { shouldShowFilterToggle, isProductGridIntersecting, triggerPointRef, activeFilters, clearAllSearchFilters } = useProductGridFilters({
    filters,
    showSkeleton,
  });
  const { handleSortBy } = useProductGridSortBy();
  const { currentStorefront } = useShopStorefrontContext();

  const isMobileOrTablet = useIsMobileOrTablet();
  const showRefinementPanelToggle = !isMobileOrTablet && shouldShowFilterToggle && !disableFilters;
  const showFloatingRefinementPanelToggle = isMobileOrTablet && shouldShowFilterToggle && isProductGridIntersecting && !disableFilters && !hideFilterToggle;
  const showGridHeader = !!lineageTitles.length && !hideHeader;

  const { onLoadComplete } = useProductGridHealth();
  useEffect(() => {
    if (products) {
      onLoadComplete();
    }
  }, [products, onLoadComplete]);

  const { isMobile, telemetryPageValue } = props;

  return (
    <StyledRoot
      __css={styles.root}
      maxWidth={maxWidth}
      $isAdminView={isAdminView}
      ref={triggerPointRef}
    >
      {showGridHeader && (
        <>
          <Box
            __css={{
              paddingX: 6,
              paddingBottom: { _: 4, md: 0 },
              width: '100%',
              paddingTop: { _: 4, md: 0 },
            }}
          >
            {!selectedCategory?.isPopularStoreCategory && (
              <Breadcrumbs
                defaultAlign="center"
                isMobile={isMobile}
                categoryId={selectedCategory?.id}
                hideLastBreadcrumb={false}
                telemetryPageValue={telemetryPageValue}
                endElement={
                  showRefinementPanelToggle ? (
                    <RefinementPanelTopBarToggle
                      isMobileOrTablet={false}
                      sortBy={sortBy}
                      handleOnSortChange={handleSortBy}
                    />
                  ) : null
                }
              />
            )}
          </Box>
          <SelectedCategoryText isDepartment={false} />
        </>
      )}

      {showRefinementPanelToggle && (
        <RefinementPanelHeaderToggle
          isMobileOrTablet={false}
          sortBy={sortBy}
          handleOnSortChange={handleSortBy}
          marginBottom={showValuePropsSection ? 6 : 10}
        />
      )}

      {showFloatingRefinementPanelToggle && (
        <FloatingRefinementPanelToggle
          sortBy={sortBy}
          handleOnSortChange={handleSortBy}
        />
      )}

      <Wrapper isEmptyState={products.length === 0 && currentStorefront === CatalogStorefront.baby}>
        {!disableFilters &&
          (!isMobileOrTablet ? (
            <RefinementsPanel
              filters={filters}
              showSkeleton={!filters.length}
            />
          ) : (
            <RefinementsPanelMobileDrawer
              filters={props.filters}
              showSkeleton={!props.filters.length}
            />
          ))}
        <Main marginBottom={disableGridMarginBottom ? undefined : '98px'}>
          {!disableFilters && (
            <ActiveRefinementsBar
              tags={activeFilters}
              onClearAll={clearAllSearchFilters}
              marginBottom={5}
            />
          )}
          <SkeletonGroup
            isReady={!props.showSkeleton}
            {...styles.skeletonRoot}
            placeholder={
              <ProductGridContainer
                maxColumns={maxColumns}
                applyEmptyStateStyles={false}
                isMobile={isMobile}
              >
                <PlaceholderTiles
                  count={15}
                  isMobile={isMobile}
                />
              </ProductGridContainer>
            }
          >
            <Root
              filters={filters}
              products={products}
              maxColumns={maxColumns}
              {...restProps}
            />
          </SkeletonGroup>
        </Main>
      </Wrapper>
    </StyledRoot>
  );
});
